/**
 * TestPlanEditor — Structured form editor for test plan drafts.
 *
 * Displayed in the right panel after AI generates a test plan.
 * Allows manual editing of all fields before saving to TMS.
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Chip,
  Divider,
  IconButton,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import {
  Save as SaveIcon,
  Delete as DiscardIcon,
  Add as AddIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import type { TestPlanDraft } from '../types';

interface TestPlanEditorProps {
  testPlan: TestPlanDraft;
  onChange: (updated: TestPlanDraft) => void;
  onSave: () => void;
  onDiscard: () => void;
  isSaving: boolean;
}

const PLAN_TYPES = ['Regression', 'Smoke', 'Integration', 'UAT', 'Performance', 'Security', 'Sanity', 'E2E'];
const PRIORITIES = ['Critical', 'High', 'Medium', 'Low'];

/**
 * Editable chip list — inline pattern with text input + add button.
 */
const EditableChipList: React.FC<{
  items: string[];
  onChange: (items: string[]) => void;
  placeholder?: string;
}> = ({ items, onChange, placeholder = 'Add item...' }) => {
  const [inputValue, setInputValue] = useState('');

  const handleAdd = () => {
    const trimmed = inputValue.trim();
    if (trimmed && !items.includes(trimmed)) {
      onChange([...items, trimmed]);
      setInputValue('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  };

  const handleDelete = (index: number) => {
    onChange(items.filter((_, i) => i !== index));
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.5 }}>
        {items.map((item, idx) => (
          <Chip
            key={idx}
            label={item}
            size="small"
            onDelete={() => handleDelete(idx)}
            deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
            sx={{ maxWidth: '100%' }}
          />
        ))}
      </Box>
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
        <TextField
          size="small"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          sx={{ flex: 1, '& .MuiInputBase-input': { fontSize: '0.85rem', py: 0.75 } }}
        />
        <IconButton
          size="small"
          onClick={handleAdd}
          disabled={!inputValue.trim()}
          color="primary"
        >
          <AddIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};

const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <>
    <Divider sx={{ my: 2 }} />
    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 0.5 }}>
      {title}
    </Typography>
  </>
);

const TestPlanEditor: React.FC<TestPlanEditorProps> = ({
  testPlan,
  onChange,
  onSave,
  onDiscard,
  isSaving,
}) => {
  const updateField = <K extends keyof TestPlanDraft>(field: K, value: TestPlanDraft[K]) => {
    onChange({ ...testPlan, [field]: value });
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.paper' }}>
      {/* Header */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            Test Plan Editor
          </Typography>
          <Chip label="Draft" size="small" color="warning" variant="outlined" />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<DiscardIcon />}
            onClick={onDiscard}
            disabled={isSaving}
            color="inherit"
          >
            Discard
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={onSave}
            disabled={isSaving}
          >
            Save to TMS
          </Button>
        </Box>
      </Box>

      {/* Scrollable Body */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 1.5 }}>
        {/* General Section */}
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 0.5 }}>
          General
        </Typography>

        <TextField
          fullWidth
          size="small"
          label="Name"
          value={testPlan.name}
          onChange={(e) => updateField('name', e.target.value)}
          sx={{ mb: 1.5 }}
        />

        <TextField
          fullWidth
          size="small"
          label="Description"
          value={testPlan.description}
          onChange={(e) => updateField('description', e.target.value)}
          multiline
          minRows={2}
          maxRows={4}
          sx={{ mb: 1.5 }}
        />

        <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
          <TextField
            size="small"
            label="Type"
            select
            value={testPlan.test_plan_type}
            onChange={(e) => updateField('test_plan_type', e.target.value)}
            sx={{ flex: 1 }}
          >
            {PLAN_TYPES.map((t) => (
              <MenuItem key={t} value={t}>{t}</MenuItem>
            ))}
          </TextField>

          <TextField
            size="small"
            label="Priority"
            select
            value={testPlan.priority}
            onChange={(e) => updateField('priority', e.target.value)}
            sx={{ flex: 1 }}
          >
            {PRIORITIES.map((p) => (
              <MenuItem key={p} value={p}>{p}</MenuItem>
            ))}
          </TextField>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <TextField
            size="small"
            label="Target Pass Rate (%)"
            type="number"
            value={testPlan.target_pass_rate}
            onChange={(e) => updateField('target_pass_rate', parseInt(e.target.value) || 0)}
            inputProps={{ min: 0, max: 100 }}
            sx={{ flex: 1 }}
          />
          <TextField
            size="small"
            label="Estimated Hours"
            type="number"
            value={testPlan.estimated_hours ?? ''}
            onChange={(e) => updateField('estimated_hours', e.target.value ? parseInt(e.target.value) : null)}
            inputProps={{ min: 0 }}
            sx={{ flex: 1 }}
          />
        </Box>

        {/* Objectives */}
        <SectionHeader title="Objectives" />
        <EditableChipList
          items={testPlan.objectives || []}
          onChange={(items) => updateField('objectives', items)}
          placeholder="Add objective..."
        />

        {/* Scope */}
        <SectionHeader title="Scope" />
        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
          Features In Scope
        </Typography>
        <EditableChipList
          items={testPlan.features_in_scope || []}
          onChange={(items) => updateField('features_in_scope', items)}
          placeholder="Add feature in scope..."
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, mb: 0.5, display: 'block' }}>
          Features Out of Scope
        </Typography>
        <EditableChipList
          items={testPlan.features_out_scope || []}
          onChange={(items) => updateField('features_out_scope', items)}
          placeholder="Add feature out of scope..."
        />

        {/* Criteria */}
        <SectionHeader title="Criteria" />
        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
          Entry Criteria
        </Typography>
        <EditableChipList
          items={testPlan.entry_criteria || []}
          onChange={(items) => updateField('entry_criteria', items)}
          placeholder="Add entry criterion..."
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, mb: 0.5, display: 'block' }}>
          Exit Criteria
        </Typography>
        <EditableChipList
          items={testPlan.exit_criteria || []}
          onChange={(items) => updateField('exit_criteria', items)}
          placeholder="Add exit criterion..."
        />

        {/* Strategy */}
        <SectionHeader title="Strategy" />
        <TextField
          fullWidth
          size="small"
          label="Testing Approach"
          value={testPlan.testing_approach || ''}
          onChange={(e) => updateField('testing_approach', e.target.value)}
          multiline
          minRows={2}
          maxRows={4}
          sx={{ mb: 1.5 }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
          Test Levels
        </Typography>
        <EditableChipList
          items={testPlan.test_levels || []}
          onChange={(items) => updateField('test_levels', items)}
          placeholder="Add test level..."
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, mb: 0.5, display: 'block' }}>
          Test Techniques
        </Typography>
        <EditableChipList
          items={testPlan.test_techniques || []}
          onChange={(items) => updateField('test_techniques', items)}
          placeholder="Add technique..."
        />

        {/* Risk & Gaps */}
        <SectionHeader title="Risk & Gaps" />
        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
          Risk Areas
        </Typography>
        <EditableChipList
          items={testPlan.risk_areas || []}
          onChange={(items) => updateField('risk_areas', items)}
          placeholder="Add risk area..."
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, mb: 0.5, display: 'block' }}>
          Gaps Identified
        </Typography>
        <EditableChipList
          items={testPlan.gaps_identified || []}
          onChange={(items) => updateField('gaps_identified', items)}
          placeholder="Add gap..."
        />

        {/* Test Cases */}
        {testPlan.test_case_ids_to_include && testPlan.test_case_ids_to_include.length > 0 && (
          <>
            <SectionHeader title="Test Cases" />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {testPlan.test_case_ids_to_include.map((id) => (
                <Chip key={id} label={`TC-${id}`} size="small" variant="outlined" color="info" />
              ))}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {testPlan.test_case_ids_to_include.length} test cases linked
            </Typography>
          </>
        )}

        {/* Bottom spacing */}
        <Box sx={{ pb: 3 }} />
      </Box>
    </Box>
  );
};

export default TestPlanEditor;
